import { Button } from "@/components/ui/button";
import { trpc } from "@/lib/trpc";
import { Loader2, Upload, X } from "lucide-react";
import { useRef, useState } from "react";
import { toast } from "sonner";

interface PhotoUploadProps {
  value: string;
  onChange: (key: string) => void;
  folder: "coaches" | "players" | "reports";
  shape?: "round" | "square";
  label?: string;
}

export default function PhotoUpload({
  value,
  onChange,
  folder,
  shape = "round",
  label = "Foto",
}: PhotoUploadProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const uploadMut = trpc.storage.uploadImage.useMutation();

  const handleFile = async (file: File) => {
    if (!file.type.startsWith("image/")) {
      toast.error("Selecciona una imagen válida");
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error("La imagen no puede pesar más de 5 MB");
      return;
    }
    setUploading(true);
    try {
      const base64 = await new Promise<string>((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result as string);
        r.onerror = reject;
        r.readAsDataURL(file);
      });
      const { key } = await uploadMut.mutateAsync({
        base64,
        mimeType: file.type,
        folder,
      });
      onChange(key);
      toast.success("Foto subida");
    } catch (e: any) {
      toast.error(e.message || "Error al subir");
    } finally {
      setUploading(false);
    }
  };

  const roundedClass = shape === "round" ? "rounded-full" : "rounded-xl";

  return (
    <div className="space-y-2">
      {label && (
        <div className="text-xs font-medium text-[var(--forest-deep)]/80">{label}</div>
      )}
      <div className="flex items-center gap-4">
        <div
          className={`relative h-20 w-20 shrink-0 bg-[var(--forest)]/10 border border-[var(--forest-deep)]/10 overflow-hidden flex items-center justify-center ${roundedClass}`}
        >
          {value ? (
            <img
              src={`/manus-storage/${value}`}
              alt=""
              className="h-full w-full object-cover"
            />
          ) : (
            <Upload size={18} className="text-[var(--forest)]/40" />
          )}
          {uploading && (
            <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
              <Loader2 className="animate-spin text-white" size={18} />
            </div>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) void handleFile(f);
              e.target.value = "";
            }}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={uploading}
            onClick={() => inputRef.current?.click()}
          >
            {value ? "Cambiar" : "Subir foto"}
          </Button>
          {value && (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="text-red-600 gap-1 h-auto py-1"
              onClick={() => onChange("")}
            >
              <X size={12} /> Quitar
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
